import { useEffect } from 'react';

/**
 * Detects screenshot key-combos and DevTools opening, and beacons a
 * generic-looking event to the backend (which alerts the admin Telegram).
 *
 * Ported from sro-dashboard's use-screenshot-audit. Endpoint + payload
 * names are intentionally vague so a user inspecting the Network tab
 * can't tell what they record. Failures are swallowed silently.
 */

const THROTTLE_MS = 2000;
// Generic paths — screenshots vs. devtools distinguished by URL, not body.
const SCREENSHOT_PATH = '/api/v1/client_event';
const DEVTOOLS_PATH = '/api/v1/ui_metric';
const DEVTOOLS_DIFF_PX = 160;
const DEVTOOLS_POLL_MS = 1000;

const SCREENSHOT_DIGIT_CODES: Record<string, string> = {
  Digit3: '3',
  Digit4: '4',
  Digit5: '5',
  Digit6: '6',
};

const MODIFIER_KEYS = new Set(['Shift', 'Meta', 'Alt', 'Control']);

const isPrefixState = (event: KeyboardEvent): boolean =>
  event.shiftKey && (event.metaKey || event.altKey || event.ctrlKey);

const buildPrefixLabel = (event: KeyboardEvent): string => {
  const parts: string[] = [];
  if (event.ctrlKey) parts.push('Ctrl');
  if (event.metaKey) parts.push('Cmd');
  if (event.altKey) parts.push('Alt');
  parts.push('Shift');
  return `${parts.join('+')} (prefix)`;
};

const detectFullCombo = (event: KeyboardEvent): string | null => {
  if (event.key === 'PrintScreen') {
    if (event.ctrlKey) return 'Ctrl+PrintScreen';
    if (event.altKey) return 'Alt+PrintScreen';
    if (event.shiftKey) return 'Shift+PrintScreen';
    return 'PrintScreen';
  }
  const digit = SCREENSHOT_DIGIT_CODES[event.code];
  if (digit && isPrefixState(event)) {
    const parts: string[] = [];
    if (event.ctrlKey) parts.push('Ctrl');
    if (event.metaKey) parts.push('Cmd');
    if (event.altKey) parts.push('Alt');
    parts.push('Shift', digit);
    return parts.join('+');
  }
  return null;
};

const isDevtoolsOpen = (): boolean => {
  const widthDiff = window.outerWidth - window.innerWidth;
  const heightDiff = window.outerHeight - window.innerHeight;
  // DevTools docked on the side widens the gap horizontally; docked on the
  // bottom widens it vertically. Threshold ignores normal browser chrome.
  return widthDiff > DEVTOOLS_DIFF_PX || heightDiff > DEVTOOLS_DIFF_PX;
};

const buildPayload = (keyCombo: string | null) => ({
  metric: keyCombo || null,
  url: window.location.href,
  path: window.location.pathname,
  title: document.title,
  referrer: document.referrer || null,
  user_agent: navigator.userAgent,
  platform:
    (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform || navigator.platform || null,
  language: navigator.language,
  timezone:
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : null,
  viewport: { w: window.innerWidth, h: window.innerHeight },
  screen: window.screen ? { w: window.screen.width, h: window.screen.height } : null,
  ts: Date.now(),
});

const sendBeacon = (eventType: 'screenshot' | 'devtools_open', keyCombo: string | null) => {
  const token = localStorage.getItem('hash_token');
  if (!token) return;

  const path = eventType === 'devtools_open' ? DEVTOOLS_PATH : SCREENSHOT_PATH;
  try {
    fetch(path, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(buildPayload(keyCombo)),
    }).catch(() => {});
  } catch {
    // swallow
  }
};

export function useScreenshotAudit(): void {
  useEffect(() => {
    const lastSentByCombo = new Map<string, number>();
    const fire = (combo: string | null) => {
      if (!combo) return;
      const now = Date.now();
      const last = lastSentByCombo.get(combo) || 0;
      if (now - last < THROTTLE_MS) return;
      lastSentByCombo.set(combo, now);
      sendBeacon('screenshot', combo);
    };

    // State machine for the prefix-only fallback (in case the OS swallows
    // the digit keydown before the browser sees it).
    let prefixActive = false;
    let prefixLabel: string | null = null;
    let nonModifierSeen = false;

    const onKeyDown = (event: KeyboardEvent) => {
      const fullCombo = detectFullCombo(event);
      if (fullCombo) {
        fire(fullCombo);
        nonModifierSeen = true;
        return;
      }
      if (isPrefixState(event)) {
        if (!prefixActive) {
          prefixActive = true;
          nonModifierSeen = false;
          prefixLabel = buildPrefixLabel(event);
        }
        if (!MODIFIER_KEYS.has(event.key)) {
          nonModifierSeen = true;
        }
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (prefixActive && !isPrefixState(event)) {
        if (!nonModifierSeen && prefixLabel) {
          fire(prefixLabel);
        }
        prefixActive = false;
        prefixLabel = null;
        nonModifierSeen = false;
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);

    let lastDevtoolsState = isDevtoolsOpen();
    const devtoolsTimer = window.setInterval(() => {
      const open = isDevtoolsOpen();
      if (open && !lastDevtoolsState) {
        sendBeacon('devtools_open', null);
      }
      lastDevtoolsState = open;
    }, DEVTOOLS_POLL_MS);

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.clearInterval(devtoolsTimer);
    };
  }, []);
}

export default useScreenshotAudit;
